import {CustomPreviewProps, KilnShellLayout} from '@domain/kiln-monitoring'
import {KilnShellCone} from '@domain/kiln-monitoring/src/types/kmsKilnInfo.types'
import {useMemo} from 'react'

import {KilnCone, LocationPill, KilnTube, KilnShellDefs} from './AtomicParts'
import {KilnShellParts, KilnShellPartType} from './KilnParts.types'

const DEFAULT_SHELL_WIDTH = 1344
const DEFAULT_SHELL_HEIGHT = 136

const FILTER_ID = 'filter0_i_833_12744'
const GRADIENT_ID = 'paint0_linear_833_12744'

type KilnShellProps = KilnShellLayout & {
  cones?: KilnShellCone[]
  customSvgPreview?: (props: CustomPreviewProps) => React.ReactNode
  meterToPixel?: number
  kilnLength?: number
  kilnDiameter?: number
}

export const KilnShellPart = ({
  id,
  x,
  y,
  width,
  height,
  showLocation = false,
  widthScaleFactor = 1,
  customSvgPreview,
  kilnLength,
  cones,
  kilnDiameter
}: KilnShellProps) => {
  const shellWidth = DEFAULT_SHELL_WIDTH * widthScaleFactor
  const conesBiggestDiameter =
    cones?.reduce((max, cone) => Math.max(max, cone.dimensions.diameter ?? 0), 0) ?? 0
  const biggestDiameter = Math.max(kilnDiameter ?? 0, conesBiggestDiameter, 1)

  const heightPerMeterInPixel = DEFAULT_SHELL_HEIGHT / biggestDiameter
  const widthPerMeterInPixel = shellWidth / (kilnLength ?? 1)

  const kilnHeight = kilnDiameter ? kilnDiameter * heightPerMeterInPixel : DEFAULT_SHELL_HEIGHT

  const kilnShellParts: KilnShellPartType[] = useMemo(() => {
    const defaultShell: KilnShellPartType[] = [
      {
        type: 'tube',
        width: shellWidth,
        height: DEFAULT_SHELL_HEIGHT,
        distanceFrom0: 0,
        y: 0
      }
    ]

    if (!cones?.length || !widthPerMeterInPixel || !kilnLength) {
      return defaultShell
    }

    const sections: KilnShellPartType[] = []

    const createTube = (start: number, end: number, height: number): KilnShellPartType => {
      const y = (DEFAULT_SHELL_HEIGHT - height) / 2
      return {
        type: 'tube',
        height,
        width: (end - start) * widthPerMeterInPixel,
        distanceFrom0: start * widthPerMeterInPixel,
        y
      }
    }

    const firstConeStart = cones[0].dimensions.distanceFrom0 ?? 0
    if (firstConeStart > 0) {
      sections.push(createTube(0, firstConeStart, kilnHeight))
    }

    cones.forEach((cone, index) => {
      const prevCone = cones[index - 1]
      const nextCone = cones[index + 1]

      const coneWidth = cone.dimensions.width ?? 0
      const coneDiameter = cone.dimensions.diameter ?? 0
      const coneStart = cone.dimensions.distanceFrom0 ?? 0
      const coneEnd = coneStart + coneWidth

      const prevY = sections[sections.length - 1]?.y ?? 0
      const prevDiameter = prevCone?.dimensions.diameter ?? kilnDiameter ?? 0

      const height = coneDiameter * heightPerMeterInPixel
      const y = (DEFAULT_SHELL_HEIGHT - height) / 2

      sections.push({
        type: 'cone',
        width: coneWidth * widthPerMeterInPixel,
        distanceFrom0: coneStart * widthPerMeterInPixel,
        height,
        y,
        startHeight: prevDiameter * heightPerMeterInPixel + prevY,
        prevY
      })

      const tubeEnd = nextCone?.dimensions.distanceFrom0 ?? kilnLength
      sections.push(createTube(coneEnd, tubeEnd, height))
    })

    return sections
  }, [
    widthPerMeterInPixel,
    cones,
    kilnLength,
    shellWidth,
    heightPerMeterInPixel,
    kilnHeight,
    kilnDiameter
  ])

  return (
    <svg
      id={`${id}_${KilnShellParts.KILN_SHELL}`}
      x={x}
      y={y}
      width={width * widthScaleFactor}
      height={height}
      viewBox={`0 0 ${shellWidth + 1} ${DEFAULT_SHELL_HEIGHT + 2}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {customSvgPreview ? (
        customSvgPreview({
          x,
          y,
          width: shellWidth,
          height: kilnHeight,
          meterToPixel: widthPerMeterInPixel,
          kilnLength
        })
      ) : (
        <>
          {kilnShellParts.map((section, index) =>
            section.type === 'cone' ? (
              <KilnCone
                key={index}
                section={section}
                index={index}
                gradientId={GRADIENT_ID}
                filterId={FILTER_ID}
              />
            ) : (
              <KilnTube
                key={index}
                section={section}
                index={index}
                gradientId={GRADIENT_ID}
                filterId={FILTER_ID}
              />
            )
          )}

          {showLocation && <LocationPill x={600} y={40} text="1" width={50} height={50} />}
          <KilnShellDefs
            shellWidth={shellWidth}
            kilnHeight={kilnHeight}
            gradientId={GRADIENT_ID}
            filterId={FILTER_ID}
            height={DEFAULT_SHELL_HEIGHT}
          />
        </>
      )}
    </svg>
  )
}
